// @flow
import React, {useContext, useEffect, useState} from "react";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import {Redirect} from "react-router-dom";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import ErrorPopup from "../GUI_COMMON/COMPONENTS/ErrorPopup";
import {gotSamplePickSheet} from "../ACTIONS/clientActions";
import ArchiverClientApi from "../API/archiverClientApi";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import {useHistory} from "react-router";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import LICache from "../SUPPORT/LICache";
import {SampleBadgeOffPremises} from "./SampleBadgeOffPremises";
import {SampleBadgePicked} from "./SampleBadgePicked";
import {SampleBadgeDifferentOrder} from "./SampleBadgeDifferentOrder";
import {SampleBadgeOut} from "./SampleBadgeOut";

const PickSheetPage = () => {
    
    const SORT_ORDER_MAG_LOCATION:Number = 0;
    const SORT_ORDER_ORDER_NUMBER:Number = 1;
    const SORT_ORDER_CASE_NUMBER:Number = 2;
    const DEFAULT_LOCATION_NAME:string = "Default Location";

    const [globalState, dispatch] = useContext(StoreContext);
    const [isLoading, setIsLoading] = useState(false);
    const [redirectTo, setRedirectTo] = useState();
    const [checkedItems, setCheckedItems] = useState({});
    const [samplesByMag, setSamplesByMag] = useState([]);
    const [samplesByOrderId, setSamplesByOrderId] = useState([]);
    const [rawSamplesList, setRawSamplesList] = useState([]);
    const [samplesByCaseNumber, setSamplesByCaseNumber] = useState([]);
    const [aSampleIsSelected, setASampleIsSelected] = useState(false);
    const [pickSheetDate, setPickSheetDate] = useState(new Date());
    const [orderBy, setOrderBy] = useState(SORT_ORDER_MAG_LOCATION);
    
    const history = useHistory();
    
    useEffect( () => {
        if (history.location.search) { // get the orders list 
            let orderStr = history.location.search;
            let orderList=[];
            if(orderStr && orderStr.length >0) {
                orderStr = decodeURI(orderStr);
                if(orderStr.startsWith('?'))
                    orderStr = orderStr.slice(1); // kill '?'
                if(orderStr.endsWith(','))
                    orderStr = orderStr.slice(0,-1);
                const re = /[?,]/;
                orderList = orderStr.split(re);
                orderList = orderList.map(item => Number(item));  // we want id numbers, not strings
            }
            setIsLoading(true);
            ArchiverClientApi.getSamplePickSheet(orderList)
            .then(({orders,samples}) => {
                dispatch(gotSamplePickSheet(orders,samples));
                (async () => LICache.clearRetrievalInfo())(); // IIFE -( we reload on moving back from this page)
            })
            .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
            .finally( () => setIsLoading(false));
        }
    }, [history.location.search]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(globalState.picksheet.samples) {
            const sampleList = globalState.picksheet.samples;
            setRawSamplesList(sampleList);
            setSamplesByMag(sampleList);  // samples comes ordered by mag by default from the server
            if(orderBy===SORT_ORDER_ORDER_NUMBER) {
                const samplesByOrderId = sortSamplesByOrderId(sampleList);  // order by order id too now.
                setSamplesByOrderId(samplesByOrderId);
            }
            else if(orderBy===SORT_ORDER_CASE_NUMBER) {
                const samplesByCaseNumber = sortSamplesByCaseNumber(sampleList); // order by case number too now
                setSamplesByCaseNumber(samplesByCaseNumber);
            }
        }
        setPickSheetDate(new Date());
    }, [globalState.picksheet.samples, orderBy]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        setASampleIsSelected(haveCheckedNonDisabledSamples(checkedItems));
    },[checkedItems]);
    
    //#region SORTERS

    function sortSamplesByOrderId(samples) {
        samples.sort( (a,b) => {
            if(a.parentOrderId < b.parentOrderId) return -1;
            if(a.parentOrderId > b.parentOrderId) return 1;
            return 0;
        });

        return samples;
    }

    function sortSamplesByCaseNumber(samples) {
        samples.sort( (a,b) => {
            if(a.caseNumber < b.caseNumber) return -1;
            if(a.caseNumber > b.caseNumber) return 1;
            return 0;
        });

        return samples;
    }
    
    //#endregion SORTERS
    
    //#region HANDLERS

    function handleCBChanged(e,item) {
        const isChecked = e.target.checked;
        checkOrUncheckSample(item, isChecked)
    }

    function checkOrUncheckSample(item, shouldCheck:boolean) {
        const curCheckedItems = {...checkedItems};
        if(shouldCheck) {
            curCheckedItems[item.id]=item;
        }
        else {
            delete(curCheckedItems[item.id]);
        }
        setCheckedItems(curCheckedItems); // update

        setASampleIsSelected(haveCheckedNonDisabledSamples(curCheckedItems));
    }

    function handleSelectAllCBChanged(e) {
        const isChecked = e.target.checked;
        let curCheckedItems = {...checkedItems};

        if(isChecked) { // add all to checked list
            if(samplesByMag && samplesByMag.length>0) {
                samplesByMag.forEach(item => {
                    if(sampleCanBeSelected(item)) {
                        // only add if not out or needs attention
                        curCheckedItems[item.id] = item;
                    }
                });
            }
        }
        else { // remove all from checked list
            curCheckedItems={};
        }
        setCheckedItems(curCheckedItems); // update

        setASampleIsSelected(haveCheckedNonDisabledSamples(curCheckedItems));
    }
    
    function sampleCanBeSelected(item) {
        return !sampleIsMarkedAsOutOrDeleted(item) && !sampleIsMarkedAsNeedsAttention(item) &&
            !sampleIsOffPremises(item);
    }

    function handlePrintPage() {
        window.print();
    }

    function handleMarkSamplesOut() {
        const ids = Object.keys(checkedItems).map( (key) => {
            const theSample = checkedItems[key];
            return {sampleId: Number(key), orderId: Number(theSample.parentOrderId)};  // returning an object per item
        });

        Popup.showConfirm("Remove Samples?",
            "<p>Are you sure you want to remove the selected samples from their mags?</p>"+
            "<p>*this may take some time if picking many samples</p>","Yes, Remove!",
            null,() => doRemoveSamplesAtServer(ids));
    }
    
    //#endregion HANDLERS

    //#region OUTPUT_FUNCTIONS
    
    function dumpOrderNumberAndDest(orderNumber, sendTo) {
        return <div className="text-right">
                <span className="li-font-small d-block ml-2">
                    <i>ORDER:</i> <strong>{LIUtils.prettyOrderNumber(orderNumber)}</strong><br/>
                <span className="badge li-bg-light ml-2">{sendTo??"-"}</span>
                </span>
        </div>;
    }
    
    function dumpSampleAsOutOrOffPremises(item,key, showOrder:boolean=true, showLocation:boolean=true, caseIsRepeated:boolean=false) {
        return (
            <tr key={key}>
                <td>
                    <input type="checkbox" className="m-2 large align-self-center li-sample-cb" name={"retrieval-cb-"+key}
                           aria-label="Checkbox" disabled={true} checked={item.datePickCompleted!==null}/>
                </td>
                <td>
                    <span style={{textDecoration:"line-through"}}>
                        {buildPickSheetSampleDetailsStr(item.barcode, item.caseNumber, item.sampleNumber, caseIsRepeated)}
                    </span>
                </td>
                <td/>
                <td className="text-right">
                    {item.isOffPremises && <SampleBadgeOffPremises/>}
                    {item.datePickCompleted!==null &&
                        <SampleBadgePicked/>}
                    {item.datePickCompleted===null &&
                        <SampleBadgeDifferentOrder/> }
                    {!item.isOffPremises && <SampleBadgeOut onClickHandler={() => ArchiverGuiUtils.GetSampleOutInfo(item)}/>}
                </td>
                
                <td>
                    {showOrder && dumpOrderNumberAndDest(item.parentOrderId,showLocation?item.sentToBrief:"")}
                </td>
            </tr>
        );
    }
    
    function dumpSampleAsNeedsAttention(item,key, showOrder:boolean=true, showMag:boolean=false, showLocation:boolean=true, caseIsRepeated:boolean=false) {
        return (
            <tr key={key}>
                <td>
                    <input type="checkbox" className="m-2 large align-self-center li-sample-cb" name={"retrieval-cb-"+key}
                           aria-label="Checkbox" disabled={true}/>
                </td>
                <td>
                    <div className="li-fg-danger font-weight-bold">
                        {buildPickSheetSampleDetailsStr(item.barcode, item.caseNumber, item.sampleNumber, caseIsRepeated, true)}
                       {/*  <span className="badge badge-warning align-self-center mr-3"
                            data-toggle="tooltip" data-placement="auto" title="fix in RO to pick">*NEEDS ATTENTION
                        </span> */}
                    </div>
                </td>
                <td>
                    {showMag && <span className="">
                            <i className="fa fa-caret-right mx-2 li-fg-primary"/>
                        {buildMagString(item.magBarcode)}
                        </span>
                    }
                </td>
                <td>
                    <span className=""><i className="fa fa-caret-right mx-2 li-fg-success"/>Section&nbsp;
                        <strong>{LIUtils.convertMagSectionIndexToAlpha(item.magSection)}</strong>
                    </span> / <strong>{item.positionAbsolute}</strong>
                </td>
                <td>
                    {showOrder && dumpOrderNumberAndDest(item.parentOrderId,showLocation?item.sentToBrief:"")}
                </td>
            </tr>
        );
    }

    function buildPickSheetSampleDetailsStr(barcode:string, caseNumber:string, sampleNumber:string, caseIsRepeated:boolean=false, isNeedsAttention:boolean=false) {
        if(caseNumber && caseNumber!=="") {
            return( 
                <div className="row m-0 p-0">
                    <div className="col">{caseIsRepeated===false ? caseNumber : ""}</div>
                    <div className="col">
                        <span className="font-italic font-weight-normal text-center">{
                                (LIUtils.existsAndNotEmpty(sampleNumber) ? sampleNumber : (LIUtils.existsAndNotEmpty(barcode) ? "BC:"+barcode : ""))
                                }
                                {isNeedsAttention && 
                        <span className="badge badge-warning align-self-center mr-3 li-pointer-default"
                            data-toggle="tooltip" data-placement="auto" title="fix in RO to pick">* NEEDS ATTENTION
                        </span>
                    }</span>
                    </div>
                    
                </div>
            );
        }
        if (barcode && barcode !== "") {
            return barcode;
        }
        return("unknown");
    }
    
    function dumpInterMagSpacer(key) {
        return(
            <tr key={key}>
                <td colSpan="5" className="text-center li-font-tiny">--- new mag ---</td>
            </tr>
        );
    }
    
    function dumpSampleAsIn(item,key,showOrder:boolean=true, showMag:boolean=false, showLocation:boolean=true, caseIsRepeated:boolean=false) {
        return(
            <tr key={key}>
                <td>
                    <input type="checkbox" className="m-2 large align-self-center li-sample-cb" name={"retrieval-cb-"+item.id}
                            aria-label="Checkbox" onChange={(e) => handleCBChanged(e,item)}
                            checked={checkedItems[item.id]??false}/>
                </td>
                <td>
                    <strong>{buildPickSheetSampleDetailsStr(item.barcode,item.caseNumber, item.sampleNumber,caseIsRepeated)}</strong>
                </td>
                <td>
                    {showMag && <span className="">
                            <i className="fa fa-caret-right mx-2 li-fg-primary"/>
                        {buildMagString(item.magBarcode)}
                        </span>
                    }
                </td>
                <td>
                    <span className=""><i className="fa fa-caret-right mx-2 li-fg-success"/>Section&nbsp;
                        <strong>{LIUtils.convertMagSectionIndexToAlpha(item.magSection)}</strong>
                    </span> / <strong>{item.positionAbsolute}</strong>
                </td>
                <td>
                    {showOrder && dumpOrderNumberAndDest(item.parentOrderId,showLocation?item.sentToBrief:"")}
                </td>
            </tr>
        );
    }
    
    function buildMagString(magBarcode:string,samplesForMagCounts:[]=[]) {
        if(magBarcode===null) {
            return(<strong>Mag: none</strong>);
        }
        const countDetails = buildSampleCountsElement(samplesForMagCounts,globalState.sampleTypes);
        const [year,box,position]=magBarcode.split(".");
        return <span className="li-bg-light p-1 rounded">
                    <strong className="">Year</strong>: {year}
                    <strong className="ml-2">Box:</strong> {box}
                    <strong className="ml-2">Magazine:</strong> {position}
                    {countDetails}
                </span>;
    } 
    
    function buildSampleCountsElement(sampleCounts, sampleTypes):Element {
        const countDetails = sampleCounts.map( (count, index) => {
            const itemKey = `count-${index}${sampleTypes.id}`;
            if(count===0) return null; // ignore
            return <div key={itemKey} className="d-inline-block text-muted ml-4">{sampleTypes.items[index].name}: <strong>{count}</strong></div>;
        });
        return <>{countDetails}</>; 
    }
    
    function buildTotalSampleCount(sampleCounts) {
        return sampleCounts.reduce((acc, curr) => acc + curr, 0);
    }
    
    function buildMagLocationString(locationName) {
        let locStr = locationName;
        if(locationName===null || locationName==="-") {
            locStr = DEFAULT_LOCATION_NAME;
        }
        return  <td colSpan="5">
                    <h3>
                        <i className="fa fa-map-marked-alt li-fg-success mr-2"/>
                        <span className="font-weight-normal">Stored at: </span>
                        <span className="li-fg-dark li-font-medium">{locStr}</span>
                    </h3>
                </td>;
    }

    function buildOrderHeaderString(orderNumber:number,destination:string,samplesForOrderForTypes:[]=[]) {
        let orderStr = LIUtils.prettyOrderNumber(orderNumber);
        let requestedBy = "-"
        if(orderNumber===null || orderNumber==="-") {
            orderStr = "-";
        }
        else { // get out requested by
            requestedBy = getOrderRequestedBy(orderNumber,globalState.picksheet.orders);
        }
        const countDetails = buildSampleCountsElement(samplesForOrderForTypes,globalState.sampleTypes);
        
        return (
            <td colSpan="4">
                <h3 className="d-flex justify-content-between">
                    <div>
                        <i className="fa fa-file-alt li-fg-success mr-2"/>
                        <span className="font-weight-normal">Order #: </span>{orderStr}
                        <div className="li-font-small text-right">Requested By: {requestedBy}</div>
                    </div>
                    <div className="d-inline-block li-font-small">{countDetails}</div>
                    {dumpDestinationHeaderString(destination)}
                </h3>
            </td>
        );
    }

    function buildCaseHeaderString(caseNumber, sampleCountsForCase=[]) {
        let caseStr = String(caseNumber);
        if(caseNumber===null || caseNumber==="-") {
            caseStr = "-";
        }
        const countDetails = buildSampleCountsElement(sampleCountsForCase, globalState.sampleTypes);
        const totalNumber = buildTotalSampleCount(sampleCountsForCase);
        
        return (
            <td colSpan="4">
                <h3 className="d-flex justify-content-between">
                    <div>
                        <i className="fa fa-folder li-fg-success mr-2"/>
                        <span className="font-weight-normal">Case #: </span>{caseStr}
                    </div>
                    <div className="d-inline-block li-font-small">Qty: {totalNumber}</div>
                    <div className="d-inline-block li-font-small">{countDetails}</div>
                </h3>

            </td>
        );
    }
    
    function dumpDestinationHeaderString(sendTo) {
        return (
            <span className="badge align-self-center li-bg-light p-3">{sendTo??"-"}</span>
        );
    }

    function showSamplesOrderedByLocationAndMag() {

        if(!isOrderingByLocation()) { // don't show any of this.
            return;
        }

        if(!samplesByMag || samplesByMag.length===0) {
            return <tr>
                <td colSpan="5">
                    <i className="fa fa-exclamation-circle li-fg-danger mr-2"/>No samples found to pick.
                </td>
            </tr>;
        }

        const retVal=[];
        let curMagBarcode = undefined;
        let curMagLocation = undefined;
        samplesByMag.forEach( (sample,index) => {
            if(curMagLocation!==sample.magLocationName) { // dump out the location change
                curMagLocation=sample.magLocationName;
                retVal.push(<tr className="mt-3 border-bottom" key={"LOC"+curMagBarcode}>
                    {buildMagLocationString(curMagLocation)}</tr>);
                curMagBarcode=undefined;
            }
            if(curMagBarcode!==sample.magBarcode) { // dump out the mag change
                curMagBarcode = sample.magBarcode;
                // this is a bit of a hack and slow -- but allows us to include the counts in the header
                retVal.push(constructOrderSamplesForMagBarcode(curMagBarcode));
            }
        });

        return retVal;
    }

    function constructOrderSamplesForMagBarcode(curMagBarcode) {
        const retVal=[];
        const sampleForMagCounts = buildSampleCountsForMag(curMagBarcode,samplesByMag,globalState.sampleTypes);

        // header
        retVal.push(<tr className="mt-3 ml-5 li-bg-light" key={"M" + curMagBarcode}
                        style={{borderTop: "4px solid black"}}>
                    <td/>
                    <td colSpan="4">{buildMagString(curMagBarcode,sampleForMagCounts)}</td>
                    </tr>);


        // build the samples for each type for this mag
        globalState.sampleTypes.items.forEach( (sampleType) => {
            let lastCaseNum=undefined;
            const samplesPerMagForType = samplesByMag.filter( item => item.magBarcode===curMagBarcode && item.sampleType===sampleType.id);
            samplesPerMagForType.forEach( (sample,index) => {
                const itemKey = index+sampleType.id;
                let caseIsRepeated=true;
                if(lastCaseNum!==sample.caseNumber) {
                    lastCaseNum=sample.caseNumber;
                    caseIsRepeated=false;
                }
                if(sampleIsMarkedAsOutOrDeleted(sample)) {
                    retVal.push(dumpSampleAsOutOrOffPremises(sample,itemKey,true, true, caseIsRepeated));
                }
                else if(sampleIsOffPremises(sample)) { // this is before NA for a reason
                    retVal.push(dumpSampleAsOutOrOffPremises(sample, itemKey, true, true, caseIsRepeated));
                }
                else if(sampleIsMarkedAsNeedsAttention(sample)) {
                    retVal.push(dumpSampleAsNeedsAttention(sample,itemKey,true, false,true, caseIsRepeated));
                }
                else {
                    retVal.push(dumpSampleAsIn(sample,itemKey,true,false, true, caseIsRepeated));
                }
            });
        });
        
        return retVal;
    }
    
    function buildSampleCountsForMag(magBarcode, samplesByMag, sampleTypes) {
        const sampleCountsByType = sampleTypes.items.map( (sampleType) => {
            return samplesByMag.reduce( (prevVal, sampleItem) => {
                if (sampleItem.sampleType === sampleType.id && sampleItem.magBarcode===magBarcode) {
                    return prevVal + 1;
                }
                return prevVal;
            },0);
        });
        return sampleCountsByType;
    }

    function showSamplesOrderedByOrderNumber() {
        if(!isOrderingByOrderNumber()) { // don't show any of this.
            return;
        }

        if(!samplesByOrderId || samplesByOrderId.length===0) {
            return <tr>
                <td colSpan="5">
                    <i className="fa fa-exclamation-circle li-fg-danger mr-2"/>No samples found to pick.
                </td>
            </tr>;
        }

        let retVal=[];
        let curOrderId = undefined;
        samplesByOrderId.forEach( (sample,index) => {
            if(curOrderId!==sample.parentOrderId) { // changing the current order
                curOrderId = sample.parentOrderId;
                // this is a bit of a hack and slow -- but allows us to include the counts in the header
                retVal.push(constructOrderSamplesForOrderId(curOrderId, sample.sentToBrief));
            }
        });

        return retVal;
    }
    
    function constructOrderSamplesForOrderId(curOrderId, locationShort) {
        const retVal=[];
        const sampleCountsForOrderForTypes = buildSampleCountsForOrder(curOrderId,samplesByOrderId,globalState.sampleTypes);

        // header
        retVal.push(<tr className=" li-border-dark-top" key={"DEST"+curOrderId}>
            {buildOrderHeaderString(curOrderId,locationShort,sampleCountsForOrderForTypes)}
        </tr>);
        
        // add order notes (if there are any)
        const orderNotes = getOrderNotes(curOrderId, globalState.picksheet.orders);
        if (orderNotes && orderNotes.length > 0) {
            retVal.push(<tr className="li-bg-light li-border-left-dark" key={"ORDER_NOTES"+curOrderId}>
                <td colSpan={4}>
                    <div className="li-font-small"><b><u>Notes</u></b>
                    {orderNotes.split("\n").map((line, index) => <div key={index}>{line}</div>)}
                    </div>  
                </td>
            </tr>);
        }
        
        // build the samples for each type for this mag
        globalState.sampleTypes.items.forEach( (sampleType) => {
            let magBarcode = undefined
            let lastCaseNum = undefined;
            const slidesForOrder = samplesByOrderId?.filter(item => item.parentOrderId === curOrderId && item.sampleType === sampleType.id) ?? [];
            slidesForOrder.forEach((sample, index) => {
                const itemKey = `${index}${sampleType.id}`;
                let caseIsRepeated = true;
                if (lastCaseNum !== sample.caseNumber) {
                    lastCaseNum = sample.caseNumber;
                    caseIsRepeated = false;
                }
                if (magBarcode !== sample.magBarcode) {
                    if (magBarcode !== undefined) { // changing mags and not first one -- space
                        retVal.push(dumpInterMagSpacer(`spacer${itemKey}`));
                    }
                    magBarcode = sample.magBarcode;
                }
                if (sampleIsMarkedAsOutOrDeleted(sample)) {
                    retVal.push(dumpSampleAsOutOrOffPremises(sample, itemKey, false, true, caseIsRepeated));
                }
                else if(sampleIsOffPremises(sample)) { // this is before NA for a reason
                    retVal.push(dumpSampleAsOutOrOffPremises(sample, itemKey, false, true, caseIsRepeated));
                }
                else if(sampleIsMarkedAsNeedsAttention(sample)) {
                    retVal.push(dumpSampleAsNeedsAttention(sample, itemKey, false, true,true, caseIsRepeated));
                }
                else {
                    retVal.push(dumpSampleAsIn(sample, itemKey, false, true, true, caseIsRepeated));
                }
            });
        });
        return retVal;
    }

    function buildSampleCountsForOrder(orderId, samplesByOrderId, sampleTypes) {
        const sampleCountsByType = sampleTypes.items.map( (sampleType) => {
            return samplesByOrderId.reduce( (prevVal, sampleItem) => {
                if (sampleItem.sampleType === sampleType.id && sampleItem.parentOrderId===orderId) {
                    return prevVal + 1;
                }
                return prevVal;
            },0);
        });
        return sampleCountsByType;
    }

    function buildSampleCountsForCase(caseNumber, samplesByCaseNumber, sampleTypes) {
        const sampleCountsByType = sampleTypes.items.map( (sampleType) => {
            return samplesByCaseNumber.reduce( (prevVal, sampleItem) => {
                if (sampleItem.sampleType === sampleType.id && sampleItem.caseNumber===caseNumber) {
                    return prevVal + 1;
                }
                return prevVal;
            },0);
        });
        return sampleCountsByType;
    }

    function showSamplesOrderedByCaseNumber() {
        if(!isOrderingByCaseNumber()) { // don't show any of this.
            return;
        }

        if(!samplesByCaseNumber || samplesByCaseNumber.length===0) {
            return <tr>
                <td colSpan="5">
                    <i className="fa fa-exclamation-circle li-fg-danger mr-2"/>No samples found to pick.
                </td>
            </tr>;
        }

        let retVal=[];
        let curCaseNumber = undefined;
        samplesByCaseNumber.forEach( (sample,index) => {
            if(curCaseNumber!==sample.caseNumber) { // changing the current caseNumber
                curCaseNumber = sample.caseNumber;
                // this is a bit of a hack and slow -- but allows us to include the counts in the header
                retVal.push(constructOrderSamplesForCaseNumber(curCaseNumber));
            }
        });

        return retVal;
    }

    function constructOrderSamplesForCaseNumber(curCaseNumber) {
        const retVal = [];
        const sampleCountsForCase = buildSampleCountsForCase(curCaseNumber, samplesByCaseNumber, globalState.sampleTypes);

        // header
        retVal.push(<tr className=" li-border-dark-top" key={"DEST" + curCaseNumber}>
            {buildCaseHeaderString(curCaseNumber, sampleCountsForCase)}
        </tr>);

        // build the samples for each type for this case
        let magBarcode = undefined
        let lastCaseNum = undefined;
        let caseIsRepeated = true; // never show the case number
        samplesByCaseNumber.forEach((sample, index) => {
            if(sample.caseNumber!==curCaseNumber) {
                return; // skip
            }
            
            if (lastCaseNum !== sample.caseNumber) {
                lastCaseNum = sample.caseNumber;
            }
            if (magBarcode !== sample.magBarcode) {
                if (magBarcode !== undefined) { // changing mags and not first one -- space
                    retVal.push(dumpInterMagSpacer("spacer" + index));
                }
                magBarcode = sample.magBarcode;
            }
            if (sampleIsMarkedAsOutOrDeleted(sample)) {
                retVal.push(dumpSampleAsOutOrOffPremises(sample, index, true, true, caseIsRepeated));
            }
            else if(sampleIsOffPremises(sample)) { // this is before NA for a reason
                retVal.push(dumpSampleAsOutOrOffPremises(sample, index, true, true, caseIsRepeated));
            }
            else if(sampleIsMarkedAsNeedsAttention(sample)) {
                retVal.push(dumpSampleAsNeedsAttention(sample, index, true, true, true, caseIsRepeated));
            }
            else {
                retVal.push(dumpSampleAsIn(sample, index, true, true, true, caseIsRepeated));
            }
        });

        return retVal;
    }

    function dumpTotalSampleCounts() {
        const sampleCountsMap = globalState.sampleTypes.items.map( (sampleType, index) => {
            const count = rawSamplesList.reduce( (prevVal, sampleItem) => {
                    if (sampleItem.sampleType === sampleType.id) {
                        return prevVal + 1;
                    }
                    return prevVal;
                },0);
            if(count===0) return null; // ignore
            const itemKey = index+sampleType.id;
            return  <div key={itemKey} className="p-0 m-0">Total {sampleType.name}: <strong>{count}</strong></div>
        });
        return (
            <div id="psSampleCountList" className="p-0 m-0 medium text-right">
                {sampleCountsMap}
            </div>
        );
    }

    function dumpSelectAll() {
        return(
            <div>
                <input type="checkbox" className="mt-4 ml-2 large align-self-center li-sample-cb" name={"retrieval-cb-all"}
                       aria-label="Checkbox" onChange={(e) => handleSelectAllCBChanged(e)}
                />
                <span className="ml-3">Select All Samples</span>
            </div>

        );
    }
    
    //#endregion OUTPUT_FUNCTIONS
    
    //#region HELPERS
    
    function getOrderRequestedBy(orderNumber, orders):string {
        const order = orders.find( item => item.id===orderNumber);
        if(order) {
            return order?.requester?.fullName??"-";
        }
        return "-";
    }
    
    function getOrderNotes(orderNumber, orders):string {
        const order = orders.find( item => item.id===orderNumber);
        if(order) {
            return order?.notes??"";
        }
        return "";
    }
    
    function markSamplesAsRemoved(sampleIds:[]) {
        sampleIds.forEach(removedSampleId => {
            const s = samplesByMag.find(sample => sample.id === removedSampleId);
            if (s) {
                s.magBarcode = "";  // fake it's out
                s.datePickCompleted = Date.now();       // fake it - just something not null
            }
        });
        setCheckedItems({});

        let notPicked = samplesByMag.filter(s => (s.magBarcode && s.magBarcode !== "") || s.datePickCompleted === null);
        if (notPicked.length===0) {
            // we are part of the Popup() so we need to use the ErrorPopup here -- hack.
            ErrorPopup.showErrorTimed("","Pick Sheet is Complete", () => {
                setRedirectTo("/retrievals");
            }, 1000);
            
            // removed the extra "complete" popup - as per AR-366 (251203) -pott
            //setRedirectTo("/retrievals");
        }
    }

    function markSamplesAsAlreadyOut(sampleIds:[]) {
        sampleIds.forEach(alreadyOutSampleId => {
            const s = samplesByMag.find(sample => sample.id === alreadyOutSampleId);
            if (s) {
                s.magBarcode = "";          // fake it's out
                s.datePickCompleted = null;
            }
        });
        setCheckedItems({});
    }
        
    function doRemoveSamplesAtServer(ids) {
        setIsLoading(true);
        ArchiverClientApi.sendPickSamplesRequest(ids)
        .then( ({samplesRemoved, samplesAlreadyOut, samplesNotComplete, samplesOffPremises}) => {
            uncheckSelectAll();
            markSamplesAsRemoved(samplesRemoved);
            markSamplesAsAlreadyOut(samplesAlreadyOut);
            if(samplesAlreadyOut && samplesAlreadyOut.length>0) {
                ErrorPopup.showError("Samples Already Out", 
                        `<p>There were ${samplesAlreadyOut.length} samples already marked as OUT and could not be picked.</p>`+
                    "<p>These samples remain unchecked in the pick sheet.</p>"
                );
            }
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function uncheckSelectAll() {
        const cb = document.getElementsByName("retrieval-cb-all");
        if(cb && cb.length>0) {
            cb[0].checked = false;
        }
    }
    
    function isOrderingByLocation() {
        return orderBy===SORT_ORDER_MAG_LOCATION;
    }

    function isOrderingByOrderNumber() {
        return orderBy===SORT_ORDER_ORDER_NUMBER;
    }

    function isOrderingByCaseNumber() {
        return orderBy===SORT_ORDER_CASE_NUMBER;
    }

    function haveCheckedNonDisabledSamples(checkedItemsList) {
        const ids = Object.keys(checkedItemsList);
        const nonDisabled = ids?.filter(i => {
            const v = document.getElementsByName("retrieval-cb-" + i);
            if(!v || v.length===0) return true;  // true == keep
            const disabled = v[0].disabled;
            return !disabled;  // keep

        })??[];
        return nonDisabled.length>0;
    }

    function sampleIsMarkedAsOutOrDeleted(item) {
        if(!item || !item.magBarcode || item.magBarcode==="") {
            return true; // out
        }
        return !!item.isDeleted;
    }

    function sampleIsMarkedAsNeedsAttention(item) {
        let needsAttention=!LIUtils.SampleHasCriticalInfo(item.caseNumber,item.sampleNumber,item.profileId);
        if (item.needsAttention && LIUtils.SampleIsNoCaseSampleRequired(item, globalState.profiles)) { // no case sample required - override so we can show the checkbox
            needsAttention = false;
        }
        return needsAttention;
    }
    
    function sampleIsOffPremises(item) {
        return item.isOffPremises;
    }
    
    //#endregion HELPERS
    
    if(redirectTo) {
        return (
            <Redirect push to={redirectTo}/>
        );
    }
    
    return(
        <div className="container li-font-medium">
            <Header/>
            <div className="li-header-row my-4">
                <BackButton/>
                <h1 className="ml-2 d-inline-block align-middle">Sample Pick Sheet</h1>
            </div>
            
            <div className="row m-0 p-0 justify-content-between">
                <div>
                    <h3>Date: {LIUtils.dateToShortDateTimeString(pickSheetDate)}</h3>
                    <p className="li-font-small m-0 p-0"><strong>Generated By:</strong> {globalState.clientInfo.fullName}</p>
                </div>
                <div id="pickSheetSortList" className="li-font-med-small">
                    <span>Sort by</span>
                    <span id="psSortByMagLocation" className={"btn-link li-pointer ml-1 "+(isOrderingByLocation()?"li-text-underline font-weight-bold":"")} onClick={() => setOrderBy(SORT_ORDER_MAG_LOCATION)}>Mag Location</span> |
                    <span id="psSortByOrderNumber" className={"btn-link li-pointer ml-1 "+(isOrderingByOrderNumber()?"li-text-underline font-weight-bold":"")} onClick={() => setOrderBy(SORT_ORDER_ORDER_NUMBER)}>Order #</span> |
                    <span id="psSortByCaseNumber" className={"btn-link li-pointer ml-1 "+(isOrderingByCaseNumber()?"li-text-underline font-weight-bold":"")} onClick={() => setOrderBy(SORT_ORDER_CASE_NUMBER)}>Case #</span>
                </div>
            </div>

            {isLoading &&
                <div className="text-center my-5">
                    Generating Picksheet<LoadingSpinner medium center/>
                </div>
            }
            {!isLoading &&
                <>
                <div className="row m-0 p-0 justify-content-between">
                    {dumpSelectAll()}
                    {dumpTotalSampleCounts()}
                </div>
                {/* SHOW SAMPLES ORDERED BY... */}
            
                <table className="table li-picksheet-table li-font-med-small my-3 w-100">
                    <tbody>
                        {!isLoading && showSamplesOrderedByLocationAndMag()}
                        {!isLoading && showSamplesOrderedByOrderNumber()}
                        {!isLoading && showSamplesOrderedByCaseNumber()}
                    </tbody>
                </table>

                {/* MARK ACTION BUTTONS */}
                <div className="row justify-content-around">
                    <button className="li-button li-primary li-font-medium px-4" disabled={!aSampleIsSelected} 
                            onClick={handleMarkSamplesOut}>
                        Mark Samples Picked
                    </button>
                    <button className="li-button li-primary li-font-medium px-5" onClick={handlePrintPage}>
                        Print
                    </button>
                </div>
                </>
            }
        </div>
    );
}

export default PickSheetPage;